import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TableConfigDto } from '@asset-metrix/portal-gateway-client-js';
import { AsyncNotification } from '@asset-metrix/portal-ui';
import { useMutation } from '@tanstack/react-query';
import HelloSign from 'hellosign-embedded';
import { useShallow } from 'zustand/react/shallow';

import { initiateSignature } from '../../api/portalGateway/initiateSignature';
import { useTenantInfoData } from '../../api/portalGateway/tenantInfo';
import { useWorkflowAction } from '../../hooks/useWorkflowAction';
import { useStore } from '../../store';
import { extractErrorMessage } from '../../utils/errorMessage';
import { invalidateTable } from '../../utils/queryCacheHelper';

import styles from './useDocumentSign.module.scss';

export function useDocumentSigning(
  pageId: string,
  contentId: string,
  tableConfig: TableConfigDto
) {
  const {
    data: { dropboxsignClientId },
  } = useTenantInfoData();
  const { t } = useTranslation();
  const { mutateAsync: mutateInitiateSignature } = useMutation({
    mutationFn: initiateSignature,
  });
  const client = useMemo(() => new HelloSign(), []);
  const { executeAction } = useWorkflowAction();
  const {
    addSigningRequest,
    updateSigningRequest,
    removeSigningRequest,
    removeSigningRequestInProgress,
  } = useStore(
    useShallow((state) => {
      return {
        addSigningRequest: state.addSigningRequest,
        updateSigningRequest: state.updateSigningRequest,
        removeSigningRequest: state.removeSigningRequest,
        removeSigningRequestInProgress: state.removeSigningRequestInProgress,
      };
    })
  );

  const open = useCallback(
    async (
      modalId: string,
      rowId: string,
      action: string,
      documentId: string
    ) => {
      client.on('close', () => {
        removeSigningRequestInProgress(documentId);
        client.off('close');
        client.off('sign');
      });

      try {
        addSigningRequest(documentId);
        const {
          data: { sign_url, signature_request_id },
        } = await mutateInitiateSignature({ contentId, documentId });
        updateSigningRequest(documentId, 'signing');

        client.on('sign', async () => {
          updateSigningRequest(documentId, 'finalizing');
          const notification = AsyncNotification.show(
            {
              message: t('app.table.documentSigning.finalizingNotification'),
            },
            'loading'
          );
          try {
            await executeAction(contentId, action, {
              data: { signature_request_id, document_id: documentId },
              rowIds: [rowId],
              modalId,
            });
            AsyncNotification.show(
              {
                id: notification,
                message: t('app.table.documentSigning.successNotification'),
              },
              'success'
            );
            const widgetId = tableConfig.workflowModalSchemas?.[modalId]
              ?.refreshAllTables
              ? undefined
              : contentId;
            await invalidateTable(tableConfig.cacheKey, pageId, widgetId);
          } catch (e: unknown) {
            createErrorNotification(
              t('app.table.documentSigning.finalizingErrorNotification'),
              extractErrorMessage(e),
              notification
            );
            console.error('Error while finalizing document signing', e);
          } finally {
            removeSigningRequest(documentId);
          }
        });

        client.open(sign_url, {
          clientId: dropboxsignClientId,
          skipDomainVerification: true,
        });
      } catch (e: unknown) {
        removeSigningRequest(documentId);
        createErrorNotification(
          t('app.table.documentSigning.initializationErrorNotification'),
          extractErrorMessage(e)
        );
        console.error('Error while initializing document signing', e);
      }
    },
    [
      addSigningRequest,
      client,
      contentId,
      dropboxsignClientId,
      executeAction,
      mutateInitiateSignature,
      removeSigningRequest,
      removeSigningRequestInProgress,
      t,
      updateSigningRequest,
      pageId,
      tableConfig.cacheKey,
      tableConfig.workflowModalSchemas,
    ]
  );

  return { open };
}

function createErrorNotification(
  header: string,
  errorMessage: string,
  notificationId?: string
) {
  AsyncNotification.show(
    {
      id: notificationId,
      message: (
        <div>
          <span className={styles['error-header']}>{header}</span>
          {errorMessage}
        </div>
      ),
    },
    'error'
  );
}
